import { Box, Grid, styled, Tabs, tabsClasses } from '@mui/material';
import { useCompanyRelativePath } from 'ogp/hooks';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  errorTextWrapperClassName,
  Header,
  LwBlockGridContainer,
  LwButton,
  LwFormSelect,
  LwSearchFilterInput,
  LwTab,
  Page,
  Toolbox,
  useOnScrollDown,
} from 'redesign';
import { routes } from 'Routes';
import { IconAdd } from 'assets/img';
import { useFeatureFlagContext } from 'shared/contexts/feature-flag-provider';
import { useDebounce } from 'shared/hooks/use-debounce';
import { useTranslation } from 'react-i18next';
import { LabourEngagementEmploymentType } from 'api/services/workforce/workforce-service.types';
import { EmployeeCard } from './components/employee-card/employee-card';
import { getEmploymentTypeLabel } from './utils/get-employment-type-label';
import { useGetWorkforce } from './hooks/use-get-workforce';
import { useSelectedWorkforceTab } from './hooks/use-selected-workforce-tab';

const Workforce = () => {
  const { allowBorrowedWorkforce } = useFeatureFlagContext();
  const { t } = useTranslation('web-common');
  const { selectedTab, handleTabChange } = useSelectedWorkforceTab();
  const { control, watch } = useForm<WorkforceFilterForm>({
    defaultValues: { employmentType: 'ALL', filter: '' },
  });
  const employmentTypeFilter = watch('employmentType');
  const filterValue = watch('filter');
  const debouncedFilter = useDebounce(filterValue, 500);
  const workforceQuery = useGetWorkforce(
    selectedTab,
    employmentTypeFilter === 'ALL' ? undefined : [employmentTypeFilter],
    debouncedFilter || undefined
  );
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();

  const { data } = workforceQuery;
  const flatData = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: workforceQuery,
    flatData,
  });

  return (
    <Page
      header={
        <HeaderContainer>
          <Header titleText="Personeelsbestand" />
          <WorkforceTabs value={selectedTab} onChange={handleTabChange} aria-label="tabbladen">
            <LwTab key="own" value="own" label="Eigen medewerkers" />
            {allowBorrowedWorkforce ? (
              <LwTab key="borrowed" value="borrowed" label="Ingeleende medewerkers" />
            ) : null}
          </WorkforceTabs>
        </HeaderContainer>
      }
      toolbox={
        <Toolbox>
          <Box alignSelf="flex-end">
            <LwButton
              to={generatePath(routes.EmployeeCreate)}
              startIcon={<IconAdd />}
              color="primary"
              iconColorMode="fill"
            >
              Voeg werknemer toe
            </LwButton>
          </Box>
          <SearchFilter>
            <LwSearchFilterInput
              name="filter"
              control={control}
              data-testid="workforce-search-filter"
            />
          </SearchFilter>
          <EmploymentTypeFilter>
            <LwFormSelect
              name="employmentType"
              control={control}
              label={t('workforce.employment-type-label')}
              data-testid="workforce-employment-type-filter"
              options={[
                { label: 'Alle', value: 'ALL' },
                { label: getEmploymentTypeLabel('EMPLOYEE', t), value: 'EMPLOYEE' },
                { label: getEmploymentTypeLabel('FREELANCER', t), value: 'FREELANCER' },
                { label: getEmploymentTypeLabel('TEMPWORKER', t), value: 'TEMPWORKER' },
              ]}
            />
          </EmploymentTypeFilter>
        </Toolbox>
      }
      onContentScroll={(e) => {
        fetchIfBottomScrolled(e.target as HTMLDivElement);
      }}
    >
      <LwBlockGridContainer container spacing={4}>
        <Grid container spacing={4}>
          {flatData?.map((employee) => (
            <Grid key={employee.id} item xs={6} md={4} lg={3}>
              <EmployeeCard
                key={employee.id}
                {...employee}
                lenderCompanyName={employee.lenderCompanyName ?? undefined}
                onClick={() => {
                  navigate(
                    generatePath(routes.EmployeeDetail, {
                      id: employee.id,
                    })
                  );
                }}
              />
            </Grid>
          ))}
        </Grid>
      </LwBlockGridContainer>
    </Page>
  );
};

type WorkforceFilterForm = {
  employmentType: LabourEngagementEmploymentType | 'ALL';
  filter: string;
};

const EmploymentTypeFilter = styled(Box)(() => ({
  width: 240,
  flex: '0 0 auto',
  [`& .${errorTextWrapperClassName}`]: {
    display: 'none',
  },
}));

const SearchFilter = styled(Box)(() => ({
  width: 320,
  flex: '0 0 auto',
  [`& .${errorTextWrapperClassName}`]: {
    display: 'none',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(6),
}));

const WorkforceTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 'unset',
  [`& .${tabsClasses.flexContainer}`]: {
    gap: theme.spacing(8),
  },
}));

export { Workforce };
